import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Textarea } from "../ui/textarea";

/**
 * The company context is prose — often pages of it — so it is edited in a
 * dialog rather than a settings row: the row stays a one-line summary and
 * the writing happens with room to see it.
 */
export function EditCompanyContextDialog({
  open,
  onOpenChange,
  value,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onSave: (next: string) => Promise<string | null>;
}) {
  const [draft, setDraft] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed each time it opens: the row's value is the source of truth.
  useEffect(() => {
    if (open) {
      setDraft(value);
      setError(null);
    }
  }, [open, value]);

  const save = async () => {
    setIsSaving(true);
    setError(null);
    const failure = await onSave(draft);
    setIsSaving(false);
    if (failure) {
      setError(failure);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Company context</DialogTitle>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <p className="text-sm text-muted-foreground">
            What this organization does, who it answers to, and anything eve should assume in
            every thread. Written into eve&rsquo;s prompt.
          </p>
          <Textarea
            autoFocus
            value={draft}
            disabled={isSaving}
            aria-label="Company context"
            placeholder="A fund administrator regulated by the IOMFSA, serving private equity clients across three jurisdictions…"
            rows={18}
            className="font-normal"
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <Button variant="ghost" disabled={isSaving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={isSaving || draft === value} onClick={() => void save()}>
            {isSaving ? "Saving…" : "Save context"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
